import { useContext } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HC_more from 'highcharts/highcharts-more';

import {
  AlertStatusFromValue,
  AlertStatusToScore,
} from '@/pcs/domain/entities/score';
import { IPCSRepository } from '@/pcs/domain/repositories/pcs.repository';
import { PCSOverviewContext } from '@/pcs/view/pages/pcs-overview';
import { GetPowerEfficiency } from '@/pcs/view/queries/get-power-efficiency';
import { formatNumber } from '@/utils/format/number';

import { PlotSkeleton } from '../../skeletons/PlotSkeleton';
import ChartInfoBox from '../chart-info-box';
// Shared config & custom series generator
import { getBaseChartOptions } from '../common-chart-config';
import { PlotSection } from '../plot-section';
import {
  getReferencePlotBands,
  type ReferenceArea,
} from '../reference-area-helper';

import { generatePowerEfficiencySeries } from './power-efficiency-series';

HC_more(Highcharts);

export type GetPowerEfficiencyPlotProps = {
  filters: IPCSRepository.Filters;
};

export function GetPowerEfficiencyPlot({
  filters,
}: GetPowerEfficiencyPlotProps) {
  const asset = useContext(PCSOverviewContext);

  // NOTE: assertion for asset here, as we assume the asset is always available
  const powerEfficiencyResult = GetPowerEfficiency.useQuery({
    filters,
    assetFullFrontendId: asset!.fullFrontendId,
  });

  if (powerEfficiencyResult.isLoading) {
    return <PlotSkeleton sectionName="PCS Efficiency" />;
  }
  if (powerEfficiencyResult.isError) {
    return <div>Error loading PCS timeseries data</div>;
  }

  // We get separate DCH and CHA metrics
  const { powerEfficiencyDCH, powerEfficiencyCHA } = powerEfficiencyResult.data;

  // Generate series & min for DCH
  const dchData = generatePowerEfficiencySeries(powerEfficiencyDCH);
  // Generate series & min for CHA
  const chaData = generatePowerEfficiencySeries(powerEfficiencyCHA);

  // Add reference areas for `ok`, `warning`, `critical`
  const referenceAreas: ReferenceArea[] = [
    // Reverse might mean "from" is higher than "to" if yAxis is reversed, so watch that
    { lower: 100, upper: 96, state: 'ok' },
    { lower: 96, upper: 90, state: 'info' },
    { lower: 89.999, upper: 0, state: 'warning' },
  ];

  const plotBands = getReferencePlotBands(referenceAreas);

  // We'll create separate chart configs for DCH and CHA
  const baseConfigDCH = getBaseChartOptions({
    title: `${powerEfficiencyDCH.metricName} Over Time`,
    xAxisTitle: 'Time',
    yAxisTitle: `${powerEfficiencyDCH.metricName} (%)`,
    plotBands,
  });

  // Do the same for CHA
  const baseConfigCHA = getBaseChartOptions({
    title: `${powerEfficiencyCHA.metricName} Over Time`,
    xAxisTitle: 'Time',
    yAxisTitle: `${powerEfficiencyCHA.metricName} (%)`,
    plotBands,
  });

  // Compute higher metric status across DCH & CHA
  const syncedYAxisLim = Math.min(dchData.yAxisMin, chaData.yAxisMin);

  // Merge the base config with DCH's data
  const chartOptionsDCH: Highcharts.Options = {
    ...baseConfigDCH,
    yAxis: {
      ...baseConfigDCH.yAxis,
      max: 100,
      min: syncedYAxisLim,
    },
    tooltip: {
      pointFormatter() {
        return `${this.series.name}: <strong>${formatNumber(Number(this.y), 1)} %</strong>`;
      },
    },
    series: dchData.series,
  };

  const chartOptionsCHA: Highcharts.Options = {
    ...baseConfigCHA,
    yAxis: {
      ...baseConfigCHA.yAxis,
      max: 100,
      min: syncedYAxisLim,
    },
    tooltip: {
      pointFormatter() {
        return `${this.series.name}: <strong>${formatNumber(Number(this.y), 1)} %</strong>`;
      },
    },
    series: chaData.series,
  };

  // Compute higher metric status across DCH & CHA
  const higherMetricScore = Math.max(
    AlertStatusToScore[powerEfficiencyDCH.metricStatus],
    AlertStatusToScore[powerEfficiencyCHA.metricStatus],
  );

  return (
    <PlotSection
      sectionName="PCS Efficiency"
      sectionStatus={AlertStatusFromValue[higherMetricScore]}
    >
      <div className="flex flex-row gap-x-10" id="pcs_efficiency">
        <div className="flex flex-1 flex-col gap-y-4 sm:w-3/4">
          {/* CHA chart */}
          <div className="min-h-[400px] flex-1">
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptionsCHA}
              className="w-full"
            />
          </div>
          {/* DCH chart */}
          <div className="min-h-[400px] flex-1">
            <HighchartsReact
              highcharts={Highcharts}
              options={chartOptionsDCH}
              className="w-full"
            />
          </div>
        </div>

        <ChartInfoBox
          description={`
        The <strong>charging efficiency</strong> plot shows how effectively incoming AC power from
        the grid is converted into DC power for battery storage. Conversely, the
        <strong>discharging efficiency</strong> plot captures how efficiently stored DC energy is
        converted back to AC power for grid or load usage. The PCS efficiency is an indicator for
        the health of the PCS and can reveal aging equipment.
        <br/>
        <br/>
        To quickly assess system performance, check for two types of anomalies:
        <ol>
          <li>
            <strong>Curve Consistency:</strong> Look for smooth, stable efficiency curves without
            abrupt dips or erratic fluctuations. A consistently high efficiency indicates optimal
            performance. Consistently low efficiency might indicate a problem with the PCS.
          </li>
          <li>
            <strong>Anomaly Detection:</strong> Sudden drops or significant fluctuations can
            signal potential issues with the current operation strategy.
          </li>
        </ol>
        <br/>
        <strong>Note:</strong> The efficiency is only calculated when the power surpasses 30 % of
        the rated power. Therefore periods of intermittent data can indicate periods of operation
        at lower power levels.`}
        />
      </div>
    </PlotSection>
  );
}
